import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { IDocumentTypesDto } from "app/main/pages/documents/dto/document-listing.dto";
import { IBox, IField } from "app/main/pages/settings/documenttype/document-type-settings-modal/document-type-settings.dto";
import { HttpActionState } from "app/main/pages/users/userslisting/store/users-listing.reducers";
import { Observable } from "rxjs";
import { IEconomicChart } from "../../contract-control-economics-graph/contract-control-economics.dto";
import { GetRelatedContract, IDocument, IRevenueDTO } from "../../contract-control.dto";
import { IAddMultisectionForm, IGetContractById, ITotals } from "../../contract-control.service";
import { IPeriodRenewal, IProgressablePeriodRenewal } from "../../store/dto/contract-control-view";
import { ManagerDTO } from "../../store/dto/manager-list.dto";
import { ICostForm } from "../cc-cost/cost.dto";
import { ICustomBoxFormData } from "../cc-custom-box/cc-custom-box.component";
import { IOverviewFieldForm } from "../cc-overview/cc-overview.component";
import { IProgressBar } from "../cc-progress-bar/cc-progress-bar.component";
import { IRevenueForm } from "../cc-revenue/revenue.dto";
import { IContractDetailFacade } from "./contract-detail-facade.token";
import { ContractDetailActions } from "./contract-detail.actions";
import * as contractDetailSelectors from "./contract-detail.selectors";
import { ISectionableRecipient } from "app/main/pages/edit-template/edit-template.dto";
import { IMetadataItemDto } from "app/main/pages/settings/documentfields/metadata-item.dto";

@Injectable({
  providedIn: 'root'
})

export class ContractDetailFacade implements IContractDetailFacade {

    public readonly isLoadingContract$: Observable<boolean> = this._store.select(contractDetailSelectors.fetchContractRequestState);
    public readonly selectedContract$: Observable<IGetContractById> = this._store.select(contractDetailSelectors.selectedContract);
    public readonly contractBoxes$: Observable<IBox[]> = this._store.select(contractDetailSelectors.contractBoxes);
    public readonly overviewCustomFields$: Observable<IField[]> = this._store.select(contractDetailSelectors.overviewCustomFields);
    public readonly selectedCategoryIds$: Observable<number[]> = this._store.select(contractDetailSelectors.selectedCategoryIds);
    public readonly contractProgressBar$: Observable<IProgressBar | null> = this._store.select(contractDetailSelectors.contractProgressBar);
    public readonly selectedContractTotals$: Observable<ITotals> = this._store.select(contractDetailSelectors.selectedContractTotals);
    public readonly updateContractDataRequestState$: Observable<HttpActionState> = this._store.select(contractDetailSelectors.updateContractDataRequestState);
    public readonly updateCostRequestState$: Observable<HttpActionState> = this._store.select(contractDetailSelectors.updateCostRequestState);
    public readonly deleteCostRequestState$: Observable<HttpActionState> = this._store.select(contractDetailSelectors.deleteCostRequestState);
    public readonly updateRevenueRequestState$: Observable<HttpActionState> = this._store.select(contractDetailSelectors.updateRevenueRequestState);
    public readonly deleteRevenueRequestState$: Observable<HttpActionState> = this._store.select(contractDetailSelectors.deleteRevenueRequestState);
    public readonly selectedRevenue$: Observable<IRevenueDTO> = this._store.select(contractDetailSelectors.selectedRevenue);
    public readonly fetchContractLayoutSettingsRequestState$: Observable<HttpActionState> = this._store.select(contractDetailSelectors.fetchContractLayoutSettingsRequestState);
    public readonly contractLayoutSettings$: Observable<IDocumentTypesDto | null> = this._store.select(contractDetailSelectors.contractLayoutSettings);
    public readonly contractEconomicsChartData$: Observable<IEconomicChart[]> = this._store.select(contractDetailSelectors.contractEconomicsChartData);
    public readonly fetchEconomicsChartState$: Observable<HttpActionState> = this._store.select(contractDetailSelectors.fetchEconomicsChartState);

    public readonly managersList$: Observable<{ managers: ManagerDTO[] }> = this._store.pipe(select(contractDetailSelectors.managers));
    public readonly contacts$: Observable<ISectionableRecipient[]> = this._store.pipe(select(contractDetailSelectors.contacts));
    public readonly metaDataItems$: Observable<IMetadataItemDto[]> = this._store.pipe(select(contractDetailSelectors.metaDataItems));
    public readonly relatedContracts$: Observable<GetRelatedContract[]> = this._store.pipe(select(contractDetailSelectors.relatedContracts));
    public readonly contractControlActivities$ = this._store.select(contractDetailSelectors.contractControlActivities);
    public readonly contractControlNotes$ = this._store.select(contractDetailSelectors.contractControlNotes);
    public readonly contractControlTodos$ = this._store.select(contractDetailSelectors.contractControlTodos);
    public readonly contractControlDocuments$ = this._store.select(contractDetailSelectors.contractControlDocuments);
    public readonly updateLayoutSettingsRequestState$ = this._store.select(contractDetailSelectors.updateLayoutSettingsRequestState);
    public readonly loadingTodoIds$ = this._store.select(contractDetailSelectors.loadingTodoIds);
    public readonly fetchOverviewCustomFieldsRequestState$ = this._store.select(contractDetailSelectors.fetchOverviewCustomFieldsRequestState);

    constructor(private _store: Store) { }


    fetchContract(id: number): void {
        this._store.dispatch(ContractDetailActions.fetchContract({ id }));
    }

    fetchContractBoxes(contract_control_id: number, document_type_id: number): void {
        this._store.dispatch(ContractDetailActions.fetchContractBoxes({ contract_control_id, document_type_id }));
    }
    
    fetchManagers(contract_control_id: number): void {
        this._store.dispatch(ContractDetailActions.fetchManagers({ contract_control_id }))
    }


    removeManager(manager: ManagerDTO, index: number, contract_control_id: number): void {
        this._store.dispatch(ContractDetailActions.removeManager({ manager, index, contract_control_id }));
    }

    linkManager(formData: IAddMultisectionForm): void {
        this._store.dispatch(ContractDetailActions.linkManager({ formData }));
    }


    fetchDocuments(contract_control_id: number) {
        this._store.dispatch(ContractDetailActions.fetchDocuments({ contract_control_id }));
    }


    addDocument(formData) {
        this._store.dispatch(ContractDetailActions.addDocuments({ formData }));
    }

    removeDocument(document: IDocument, index: number, contract_control_id: number) {
        this._store.dispatch(ContractDetailActions.removeDocument({ document, index, contract_control_id }));
    }

    getContractControlActivities(contract_control_id: number) {
        this._store.dispatch(ContractDetailActions.getContractControlActivities({ contract_control_id }))
    }


    getContractControlNotes(contract_control_id: number) {
        this._store.dispatch(ContractDetailActions.getContractControlNotes({ contract_control_id }))
    }

    getContractControlTodos(contract_control_id: number) {
        this._store.dispatch(ContractDetailActions.getContractControlTodos({ contract_control_id }))
    }

    getContractControlDocuments(contract_control_id: number) {
        this._store.dispatch(ContractDetailActions.getContractControlDocuments({ contract_control_id }))
    }


    updateContractData(column: string, value: string | number): void {
        this._store.dispatch(ContractDetailActions.updateContractData({ column, value }));
    }


    updateCost(costForm: ICostForm): void {
        this._store.dispatch(ContractDetailActions.updateCost({ costForm }));
    }


    deleteCost(costId: number): void {
        this._store.dispatch(ContractDetailActions.deleteCost({ costId }));
    }


    updateRevenue(revenueForm: IRevenueForm): void {
        this._store.dispatch(ContractDetailActions.updateRevenue({ revenueForm }));
    }


    deleteRevenue(revenueId: number): void {
        this._store.dispatch(ContractDetailActions.deleteRevenue({ revenueId }));
    }


    editRevenue(revenue: IRevenueDTO): void {
        this._store.dispatch(ContractDetailActions.editRevenue({ revenue }));
    }


    clearSelectedRevenue(): void {
        this._store.dispatch(ContractDetailActions.clearSelectedRevenue());
    }

    resetState(): void {
        this._store.dispatch(ContractDetailActions.resetState());
    }


    resetConnectedContracts(): void {
        this._store.dispatch(ContractDetailActions.resetConnectedContracts());
    }


    selectContract(contractId: number): void {
        this._store.dispatch(ContractDetailActions.selectContract({ contractId }));
    }


    updatePeriodRenewalProgress(periodRenewal: IPeriodRenewal | IProgressablePeriodRenewal): void {
        this._store.dispatch(ContractDetailActions.updatePeriodRenewalProgress({ periodRenewal }));
    }


    updatePeriodRenewal(periodRenewal: IPeriodRenewal | IProgressablePeriodRenewal): void {
        this._store.dispatch(ContractDetailActions.updatePeriodRenewal({ periodRenewal }));
    }


    getEconomicsChartData(): void {
        this._store.dispatch(ContractDetailActions.fetchEconomicsChartsData());
    }


    updateCustomBoxValue(formData: ICustomBoxFormData): void {
        this._store.dispatch(ContractDetailActions.updateCustomBoxValue({ formData }));
    }


    getOverviewCustomFields(contract_control_id: number): void {
        this._store.dispatch(ContractDetailActions.getOverviewCustomFields({ contract_control_id }));
    }


    updateOverviewCustomFieldValue(formData: IOverviewFieldForm): void {
        this._store.dispatch(ContractDetailActions.updateOverviewCustomFieldValue({ formData }));
    }


    getContractProgressBar(contract_control_id: number): void {
        this._store.dispatch(ContractDetailActions.getContractProgressBar({ contract_control_id }));
    }


    fetchRelatedContracts(contract_control_id: number): void {
        this._store.dispatch(ContractDetailActions.fetchRelatedContracts({ contract_control_id }));
    }


    linkRelatedContract(formData: { contract_control_id: number; related_contract_id: number }, item: GetRelatedContract): void {
        this._store.dispatch(ContractDetailActions.linkRelatedContract({ formData, item }));
    }


    unlinkRelatedContract(related_contract_id: number, contract_control_id: number): void {
        this._store.dispatch(ContractDetailActions.unlinkRelatedContract({ related_contract_id, contract_control_id }));
    }


    fetchContacts(contract_control_id: number): void {
        this._store.dispatch(ContractDetailActions.fetchContacts({ contract_control_id }));
    }

    linkContact(formData: IAddMultisectionForm): void {
        this._store.dispatch(ContractDetailActions.linkContact({ formData }));
    }

    unlinkContact(sectionable_id: number, contract_control_id: number): void {
        this._store.dispatch(ContractDetailActions.unlinkContact({ sectionable_id, contract_control_id }));
    }

    addFieldInCustomBox(box_id: number, field: IField): void {
        this._store.dispatch(ContractDetailActions.addFieldInCustomBox({ box_id, field }));
    }

    fetchMetaDataItems(): void {
        this._store.dispatch(ContractDetailActions.fetchMetaDataFields());
    }

    updateLayoutSettings(layoutSettings: IDocumentTypesDto, contract_control_id: number): void {
        this._store.dispatch(ContractDetailActions.updateLayoutSettings({ layoutSettings, contract_control_id }));
    }

    setTodoLoading(todoId: number, isLoading: boolean): void {
        this._store.dispatch(ContractDetailActions.setTodoLoading({todoId, isLoading}));
    }

    updateTodoStatus(todoId: number, newStatus: string, previousStatus: string): void {
        this._store.dispatch(ContractDetailActions.updateTodoStatus({todoId, newStatus, previousStatus}));
    }

}
